import type { ThemeMultiple } from '@shared-ui/theme/types'

export const defaultTheme = {
  COLOR_STATUS_BAR: 'light-content' as const,

  COLOR_NEUTRAL_50: '#FFFFFF',
  COLOR_NEUTRAL_100: '#F1F1F3',
  COLOR_NEUTRAL_150: '#E4E4E7',
  COLOR_NEUTRAL_200: '#E4E4E7',
  COLOR_NEUTRAL_300: '#D4D4D8',
  COLOR_NEUTRAL_400: '#9F9FA9',
  COLOR_NEUTRAL_500: '#71717B',
  COLOR_NEUTRAL_600: '#52525C',
  COLOR_NEUTRAL_700: '#3F3F47',
  COLOR_NEUTRAL_800: '#27272A',
  COLOR_NEUTRAL_900: '#18181B',
  COLOR_NEUTRAL_950: '#09090B',

  COLOR_BLUE_50: '#F1F6FB',
  COLOR_BLUE_400: '#90BAF1',
  COLOR_BLUE_500: '#4F97F0',
  COLOR_BLUE_600: '#1673F9',
  COLOR_BLUE_700: '#1765D8',
  COLOR_BLUE_950: '#0D1D32',
  COLOR_GREEN_50: '#E7FCEA',
  COLOR_GREEN_400: '#55D57A',
  COLOR_GREEN_500: '#3AAF5E',
  COLOR_GREEN_600: '#009E4F',
  COLOR_GREEN_700: '#00803F',
  COLOR_GREEN_950: '#0F2213',
  COLOR_ORANGE_50: '#FFF4EB',
  COLOR_ORANGE_400: '#FFB161',
  COLOR_ORANGE_500: '#F59401',
  COLOR_ORANGE_600: '#DA8201',
  COLOR_ORANGE_700: '#B26A00',
  COLOR_ORANGE_950: '#331B02',
  COLOR_RED_50: '#FCF3F2',
  COLOR_RED_400: '#F29D96',
  COLOR_RED_500: '#F25C58',
  COLOR_RED_600: '#EE3045',
  COLOR_RED_700: '#D51F32',
  COLOR_RED_950: '#33100F',

  COLOR_OPACITY_BLACK_100: '#09090B08',
  COLOR_OPACITY_BLACK_200: '#09090B0D',
  COLOR_OPACITY_BLACK_300: '#09090B14',
  COLOR_OPACITY_BLACK_700: '#09090BD6',

  COLOR_OPACITY_WHITE_100: '#FFFFFF0D',
  COLOR_OPACITY_WHITE_200: '#FFFFFF12',
  COLOR_OPACITY_WHITE_400: '#FFFFFF66',
  COLOR_OPACITY_WHITE_600: '#FFFFFFB2',
  COLOR_OPACITY_WHITE_700: '#FFFFFFD1',

  COLOR_BRAND_100: '#F1F6FB',
  COLOR_BRAND_200: '#0D1D32',
  COLOR_BRAND_600: '#90BAF1',
  COLOR_BRAND_700: '#1673F9',

  COLOR_SUCCESS_100: '#E7FCEA',
  COLOR_SUCCESS_200: '#0F2213',
  COLOR_SUCCESS_600: '#55D57A',
  COLOR_SUCCESS_700: '#009E4F',

  COLOR_WARNING_100: '#FFF4EB',
  COLOR_WARNING_200: '#331B02',
  COLOR_WARNING_600: '#FFB161',
  COLOR_WARNING_700: '#DA8201',

  COLOR_CRITICAL_100: '#FCF3F2',
  COLOR_CRITICAL_200: '#33100F',
  COLOR_CRITICAL_600: '#F29D96',
  COLOR_CRITICAL_700: '#EE3045',

  COLOR_CASHBACK_NOVICE_600: '#5CC182',
  COLOR_CASHBACK_NOVICE_700: '#4A9667',
  COLOR_CASHBACK_BRONZE_600: '#CD9C90',
  COLOR_CASHBACK_BRONZE_700: '#A66E60',
  COLOR_CASHBACK_SILVER_600: '#A4ADB7',
  COLOR_CASHBACK_SILVER_700: '#686E76',
  COLOR_CASHBACK_GOLD_600: '#E2C46E',
  COLOR_CASHBACK_GOLD_700: '#D2A93A',
  COLOR_CASHBACK_PLATINUM_600: '#937FDE',
  COLOR_CASHBACK_PLATINUM_700: '#59469C',

  SIZE_BLOCK_BORDER_RADIUS: 20,
  SIZE_BLOCK_BORDER_RADIUS_EXTRA: 24,

  SIZE_BUTTON_BORDER_RADIUS: 9999,
  SIZE_CONTAINER_PADDING: 24,
  SIZE_LOGOTYPE_ENTRY_HEIGHT: 110,
  SIZE_LOGOTYPE_ENTRY_WIDTH: 110,
  SIZE_MENU_BORDER_RADIUS: 50,
  SIZE_SHEET_BORDER_RADIUS: 24,
}

export const theme: ThemeMultiple = {
  default: {
    ...defaultTheme,
    ASSET_LOGOTYPE: require('@shared-ui/theme/images/light/logotype.png'),
    ASSET_LOGOTYPE_HORIZONTAL: require('@shared-ui/theme/images/light/logo-horizontal.png'),
    COLOR_BG_EMBEDDED: defaultTheme.COLOR_NEUTRAL_950,
    COLOR_BG_POPUP_OVERLAY: 'rgba(24,32,40,0.9)',
    COLOR_GRADIENT_SKELETON: ['#F1F1F2', '#D6D6D7', '#F1F1F2'],
    COLOR_GRADIENT_PRIMARY: ['#448ce7', '#0C75F7'],
    COLOR_GRADIENT_VIP: [defaultTheme.COLOR_NEUTRAL_600, defaultTheme.COLOR_NEUTRAL_900],
    COLOR_STATUS_BAR: 'dark-content',

    COLOR_BG_BASE: defaultTheme.COLOR_NEUTRAL_50,
    COLOR_BG_SURFACE: defaultTheme.COLOR_NEUTRAL_100,
    COLOR_BG_FILL: defaultTheme.COLOR_OPACITY_BLACK_200,
    COLOR_BG_FILL_SUBTLE: defaultTheme.COLOR_OPACITY_BLACK_100,
    COLOR_BG_FILL_FIXED: defaultTheme.COLOR_OPACITY_WHITE_200,
    COLOR_BG_INVERSE: defaultTheme.COLOR_NEUTRAL_800,
    COLOR_BG_DISABLED: defaultTheme.COLOR_NEUTRAL_100,
    COLOR_BG_OVERLAY_GLASS: defaultTheme.COLOR_OPACITY_WHITE_700,
    COLOR_BG_BRAND_PRIMARY: defaultTheme.COLOR_BRAND_700,
    COLOR_BG_BRAND_SECONDARY: defaultTheme.COLOR_BRAND_100,
    COLOR_BG_SUCCESS: defaultTheme.COLOR_SUCCESS_100,
    COLOR_BG_WARNING: defaultTheme.COLOR_WARNING_100,
    COLOR_BG_CRITICAL_PRIMARY: defaultTheme.COLOR_RED_700,
    COLOR_BG_CRITICAL_SECONDARY: defaultTheme.COLOR_CRITICAL_100,

    COLOR_TEXT_DISABLED: defaultTheme.COLOR_NEUTRAL_400,
    COLOR_FOCUS_RING: defaultTheme.COLOR_NEUTRAL_500,
    COLOR_ICON_DISABLED: defaultTheme.COLOR_NEUTRAL_300,
    COLOR_ICON_ON_DESTRUCTIVE: defaultTheme.COLOR_NEUTRAL_50,
    COLOR_TEXT_PRIMARY: defaultTheme.COLOR_NEUTRAL_950,
    COLOR_TEXT_SECONDARY: defaultTheme.COLOR_NEUTRAL_500,
    COLOR_TEXT_TERTIARY: defaultTheme.COLOR_NEUTRAL_400,
    COLOR_TEXT_BRAND: defaultTheme.COLOR_BLUE_700,
    COLOR_TEXT_SUCCESS: defaultTheme.COLOR_GREEN_700,
    COLOR_TEXT_WARNING: defaultTheme.COLOR_ORANGE_700,
    COLOR_TEXT_CRITICAL: defaultTheme.COLOR_RED_700,
    COLOR_TEXT_INVERSE_PRIMARY: defaultTheme.COLOR_NEUTRAL_50,
    COLOR_TEXT_INVERSE_SECONDARY: defaultTheme.COLOR_NEUTRAL_400,
    COLOR_TEXT_FIXED: defaultTheme.COLOR_NEUTRAL_50,
    COLOR_TEXT_ELEVATED_SECONDARY: defaultTheme.COLOR_OPACITY_WHITE_600,

    COLOR_TEXT_NOVICE: defaultTheme.COLOR_CASHBACK_NOVICE_700,
    COLOR_TEXT_BRONZE: defaultTheme.COLOR_CASHBACK_BRONZE_700,
    COLOR_TEXT_SILVER: defaultTheme.COLOR_CASHBACK_SILVER_700,
    COLOR_TEXT_GOLD: defaultTheme.COLOR_CASHBACK_GOLD_700,
    COLOR_TEXT_PLATINUM: defaultTheme.COLOR_CASHBACK_PLATINUM_700,

    COLOR_ICON_PRIMARY: defaultTheme.COLOR_NEUTRAL_900,
    COLOR_ICON_SECONDARY: defaultTheme.COLOR_NEUTRAL_400,
    COLOR_ICON_BRAND: defaultTheme.COLOR_BRAND_700,
    COLOR_ICON_SUCCESS: defaultTheme.COLOR_SUCCESS_700,
    COLOR_ICON_WARNING: defaultTheme.COLOR_WARNING_700,
    COLOR_ICON_CRITICAL: defaultTheme.COLOR_CRITICAL_700,
    COLOR_ICON_INVERSE: defaultTheme.COLOR_NEUTRAL_100,
    COLOR_ICON_ELEVATED_SECONDARY: defaultTheme.COLOR_OPACITY_WHITE_600,
    COLOR_ICON_FIXED: defaultTheme.COLOR_NEUTRAL_100,

    COLOR_ICON_NOVICE: defaultTheme.COLOR_CASHBACK_NOVICE_700,
    COLOR_ICON_BRONZE: defaultTheme.COLOR_CASHBACK_BRONZE_700,
    COLOR_ICON_SILVER: defaultTheme.COLOR_CASHBACK_SILVER_700,
    COLOR_ICON_GOLD: defaultTheme.COLOR_CASHBACK_GOLD_700,
    COLOR_ICON_PLATINUM: defaultTheme.COLOR_CASHBACK_PLATINUM_700,

    COLOR_STROKE_ACTIVE: defaultTheme.COLOR_NEUTRAL_500,
    COLOR_STROKE_PRIMARY: defaultTheme.COLOR_OPACITY_BLACK_200,
    COLOR_STROKE_SECONDARY: defaultTheme.COLOR_NEUTRAL_150,
    COLOR_STROKE_BASE: defaultTheme.COLOR_NEUTRAL_50,
    COLOR_STROKE_SURFACE: defaultTheme.COLOR_NEUTRAL_100,
    COLOR_STROKE_BRAND: defaultTheme.COLOR_BLUE_600,
    COLOR_STROKE_SUCCESS: defaultTheme.COLOR_GREEN_600,
    COLOR_STROKE_WARNING: defaultTheme.COLOR_ORANGE_600,
    COLOR_STROKE_CRITICAL: defaultTheme.COLOR_RED_600,
  },

  dark: {
    ...defaultTheme,
    ASSET_LOGOTYPE: require('@shared-ui/theme/images/dark/logotype.png'),
    ASSET_LOGOTYPE_HORIZONTAL: require('@shared-ui/theme/images/dark/logo-horizontal.png'),
    COLOR_BG_EMBEDDED: defaultTheme.COLOR_NEUTRAL_700,
    COLOR_BG_POPUP_OVERLAY: 'rgba(0, 0, 0, 0.8)',
    COLOR_GRADIENT_SKELETON: ['#F1F1F2', '#D6D6D7', '#F1F1F2'],
    COLOR_GRADIENT_PRIMARY: ['#448ce7', '#0C75F7'],
    COLOR_GRADIENT_VIP: [defaultTheme.COLOR_NEUTRAL_600, defaultTheme.COLOR_NEUTRAL_900],
    COLOR_STATUS_BAR: 'light-content',

    COLOR_BG_BASE: defaultTheme.COLOR_NEUTRAL_950,
    COLOR_BG_SURFACE: defaultTheme.COLOR_NEUTRAL_900,
    COLOR_BG_FILL: defaultTheme.COLOR_OPACITY_WHITE_200,
    COLOR_BG_FILL_SUBTLE: defaultTheme.COLOR_OPACITY_WHITE_100,
    COLOR_BG_FILL_FIXED: defaultTheme.COLOR_OPACITY_WHITE_200,
    COLOR_BG_INVERSE: defaultTheme.COLOR_NEUTRAL_150,
    COLOR_BG_DISABLED: defaultTheme.COLOR_NEUTRAL_900,
    COLOR_BG_OVERLAY_GLASS: defaultTheme.COLOR_OPACITY_BLACK_700,
    COLOR_BG_BRAND_PRIMARY: defaultTheme.COLOR_BRAND_600,
    COLOR_BG_BRAND_SECONDARY: defaultTheme.COLOR_BRAND_200,
    COLOR_BG_SUCCESS: defaultTheme.COLOR_SUCCESS_200,
    COLOR_BG_WARNING: defaultTheme.COLOR_WARNING_200,
    COLOR_BG_CRITICAL_PRIMARY: defaultTheme.COLOR_RED_500,
    COLOR_BG_CRITICAL_SECONDARY: defaultTheme.COLOR_CRITICAL_200,

    COLOR_TEXT_DISABLED: defaultTheme.COLOR_NEUTRAL_500,
    COLOR_FOCUS_RING: defaultTheme.COLOR_NEUTRAL_500,
    COLOR_ICON_DISABLED: defaultTheme.COLOR_NEUTRAL_500,
    COLOR_ICON_ON_DESTRUCTIVE: defaultTheme.COLOR_NEUTRAL_50,
    COLOR_TEXT_PRIMARY: defaultTheme.COLOR_NEUTRAL_50,
    COLOR_TEXT_SECONDARY: defaultTheme.COLOR_NEUTRAL_400,
    COLOR_TEXT_TERTIARY: defaultTheme.COLOR_NEUTRAL_500,
    COLOR_TEXT_BRAND: defaultTheme.COLOR_BLUE_400,
    COLOR_TEXT_SUCCESS: defaultTheme.COLOR_GREEN_400,
    COLOR_TEXT_WARNING: defaultTheme.COLOR_ORANGE_400,
    COLOR_TEXT_CRITICAL: defaultTheme.COLOR_RED_400,
    COLOR_TEXT_INVERSE_PRIMARY: defaultTheme.COLOR_NEUTRAL_950,
    COLOR_TEXT_INVERSE_SECONDARY: defaultTheme.COLOR_NEUTRAL_600,
    COLOR_TEXT_FIXED: defaultTheme.COLOR_NEUTRAL_50,
    COLOR_TEXT_ELEVATED_SECONDARY: defaultTheme.COLOR_OPACITY_WHITE_600,

    COLOR_TEXT_NOVICE: defaultTheme.COLOR_CASHBACK_NOVICE_600,
    COLOR_TEXT_BRONZE: defaultTheme.COLOR_CASHBACK_BRONZE_600,
    COLOR_TEXT_SILVER: defaultTheme.COLOR_CASHBACK_SILVER_600,
    COLOR_TEXT_GOLD: defaultTheme.COLOR_CASHBACK_GOLD_600,
    COLOR_TEXT_PLATINUM: defaultTheme.COLOR_CASHBACK_PLATINUM_600,

    COLOR_ICON_PRIMARY: defaultTheme.COLOR_NEUTRAL_100,
    COLOR_ICON_SECONDARY: defaultTheme.COLOR_NEUTRAL_500,
    COLOR_ICON_BRAND: defaultTheme.COLOR_BRAND_600,
    COLOR_ICON_SUCCESS: defaultTheme.COLOR_SUCCESS_600,
    COLOR_ICON_WARNING: defaultTheme.COLOR_WARNING_600,
    COLOR_ICON_CRITICAL: defaultTheme.COLOR_CRITICAL_600,
    COLOR_ICON_INVERSE: defaultTheme.COLOR_NEUTRAL_800,
    COLOR_ICON_ELEVATED_SECONDARY: defaultTheme.COLOR_OPACITY_WHITE_600,
    COLOR_ICON_FIXED: defaultTheme.COLOR_NEUTRAL_100,

    COLOR_ICON_NOVICE: defaultTheme.COLOR_CASHBACK_NOVICE_600,
    COLOR_ICON_BRONZE: defaultTheme.COLOR_CASHBACK_BRONZE_600,
    COLOR_ICON_SILVER: defaultTheme.COLOR_CASHBACK_SILVER_600,
    COLOR_ICON_GOLD: defaultTheme.COLOR_CASHBACK_GOLD_600,
    COLOR_ICON_PLATINUM: defaultTheme.COLOR_CASHBACK_PLATINUM_600,

    COLOR_STROKE_ACTIVE: defaultTheme.COLOR_NEUTRAL_500,
    COLOR_STROKE_PRIMARY: defaultTheme.COLOR_OPACITY_WHITE_200,
    COLOR_STROKE_SECONDARY: defaultTheme.COLOR_NEUTRAL_800,
    COLOR_STROKE_BASE: defaultTheme.COLOR_NEUTRAL_950,
    COLOR_STROKE_SURFACE: defaultTheme.COLOR_NEUTRAL_900,
    COLOR_STROKE_BRAND: defaultTheme.COLOR_BLUE_500,
    COLOR_STROKE_SUCCESS: defaultTheme.COLOR_GREEN_500,
    COLOR_STROKE_WARNING: defaultTheme.COLOR_ORANGE_500,
    COLOR_STROKE_CRITICAL: defaultTheme.COLOR_RED_500,
  },
}
